import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { getMapOverlay } from '@actions/map'
import { selectZipOverlay, selectCountyOverlay } from '@reducers/map/mapSelectors'
import { selectCoverageData, selectCurrentUserStatus } from '@reducers/currentUser/currentUserSelectors'

import { ERROR } from '@constants/fetchStatuses'
import { openErrorNotification } from '../../helpers'
import useSignupAnalyticsTracker from '@components/Signup/SegmentAnalytics/useSignupAnalyticsTracker'
import useSignupPageView from '@components/Signup/SegmentAnalytics/useSignupPageView'
import PropertyDataCollectorSetup from './PropertyDataCollectorSetup'

const PropertyDataCollectorSetupContainer = ({
  initialValues = {},
  handleSinglePageFormSubmit,
  validationErrors = [],
  onClearValidationErrors,
  submitting = false,
  shellUser,
}) => {
  useSignupPageView('Account Setup Property Data Collector')
  const track = useSignupAnalyticsTracker()

  const formRef = useRef(null)
  const coverageAreaRef = useRef(null)
  const domMoveCompletedRef = useRef(false)

  // Redux selectors
  const currentUserStatus = useSelector(selectCurrentUserStatus)
  const { coverage: initialCoverage, coverageLevel: initialCoverageLevel } = useSelector(selectCoverageData)
  const zipOverlay = useSelector(selectZipOverlay)
  const countyOverlay = useSelector(selectCountyOverlay)

  // Component state
  const [agreed, setAgreed] = useState(false)
  const [termsModalVisible, setTermsModalVisible] = useState(false)
  const [passwordRequirementsMet, setPasswordRequirementsMet] = useState(false)
  const [formValues, setFormValues] = useState({
    password: '',
    confirm_password: '',
  })
  const [{ coverage, coverageLevel }, setCoverage] = useState({
    coverage: initialCoverage || [],
    coverageLevel: initialCoverageLevel,
  })
  const [coverageError, setCoverageError] = useState(false)
  const [startingLocationDifferent, setStartingLocationDifferent] = useState(false)
  // Store credentials in ref to avoid re-renders on updates
  const credentialsRef = useRef([])
  const [credentials] = useState([])

  // Effects
  useEffect(() => {
    if (submitting && currentUserStatus === ERROR) {
      openErrorNotification()
    }
  }, [currentUserStatus, submitting])

  useEffect(() => {
    if (coverageError && coverage.length) {
      setCoverageError(false)
    }
  }, [coverage, coverageError])

  // Scroll error banner into view when validation errors appear
  useEffect(() => {
    if (validationErrors.length > 0) {
      const cardElement = document.querySelector('.step-header')
      if (cardElement) {
        cardElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }
    }
  }, [validationErrors])

  // Helper function to create validation message element
  const createValidationMessage = (message) => {
    const msg = document.createElement('div')
    msg.textContent = `* ${message}`
    msg.style.cssText = `
      color: #ff4d4f;
      font-size: 14px;
      font-weight: 400;
      margin-top: 4px;
      margin-bottom: 8px;
    `
    msg.classList.add('coverage-validation-msg')
    return msg
  }

  // Helper function to remove existing validation message
  const removeExistingValidationMessage = (header) => {
    const existingMsg = header.nextElementSibling?.classList.contains('coverage-validation-msg')
      ? header.nextElementSibling
      : null
    if (existingMsg) {
      existingMsg.remove()
    }
  }

  // Handle coverage area validation message injection
  useEffect(() => {
    const container = coverageAreaRef.current
    if (!container) {
      return
    }

    const header = container.querySelector('h3')
    if (!header) {
      return
    }

    const coverageError = validationErrors.find((error) => error.field === 'coverage')

    removeExistingValidationMessage(header)

    if (coverageError) {
      const validationMsg = createValidationMessage(coverageError.message)
      header.insertAdjacentElement('afterend', validationMsg)
    }
  }, [validationErrors])

  // Move selected coverage div to be inside main-content div
  useEffect(() => {
    // Reset the move completed flag if coverage is cleared/reset
    if (!coverage || coverage.length === 0) {
      domMoveCompletedRef.current = false
    }

    const container = coverageAreaRef.current
    if (!container) {
      return
    }

    // If we've already completed the move and have coverage data, skip the check
    if (domMoveCompletedRef.current && coverage && coverage.length > 0) {
      return
    }

    // Use setTimeout to ensure DOM is fully rendered
    const timeoutId = setTimeout(() => {
      const mainContentDiv = container.querySelector('.ant-row.main-content')
      const selectedCoverageDiv = container.querySelector('.ant-row.selected-coverage')

      if (mainContentDiv && selectedCoverageDiv && selectedCoverageDiv.parentNode !== mainContentDiv) {
        // Move the selected-coverage div to be the last child of main-content div
        mainContentDiv.appendChild(selectedCoverageDiv)
        domMoveCompletedRef.current = true
      } else if (mainContentDiv && selectedCoverageDiv) {
        domMoveCompletedRef.current = true
      }
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [zipOverlay, countyOverlay, coverage?.length]) // Re-run when coverage area structure changes

  const handleFormValuesChange = useCallback(
    (changedValues, allValues) => {
      // Handle clearing field errors
      const updatedFields = Object.keys(changedValues)
        .filter((name) => formRef.current.getFieldError(name).length)
        .map((name) => ({ name, errors: [] }))
      formRef.current.setFields(updatedFields)

      // Clear validation error summary when user starts typing
      if (validationErrors.length > 0 && onClearValidationErrors) {
        onClearValidationErrors()
      }

      // Update password form values
      setFormValues({
        password: allValues.password,
        confirm_password: allValues.confirm_password,
      })
    },
    [validationErrors, onClearValidationErrors],
  )

  const handlePasswordRequirementsChange = useCallback((met) => {
    setPasswordRequirementsMet(met)
  }, [])

  const handleCredentialsChange = useCallback((newCredentials) => {
    // Store in ref to avoid re-renders, but keep state for initial render
    credentialsRef.current = newCredentials
  }, [])

  const setTermsAgreed = useCallback(
    (value) => {
      const verbiage = value ? 'Agreed' : 'Disagreed'
      track({ eventName: `Sign Up Terms ${verbiage}` })
      setAgreed(value)
    },
    [track],
  )

  const handleAgree = useCallback(() => {
    setTermsAgreed(true)
    setTermsModalVisible(false)
  }, [setTermsAgreed])

  const handleDisagree = useCallback(() => {
    setTermsAgreed(false)
    setTermsModalVisible(false)
  }, [setTermsAgreed])

  const openTermsModal = useCallback(() => {
    track({ eventName: 'Sign Up Terms Clicked' })
    setTermsModalVisible(true)
  }, [track])

  const handleStartingLocationChange = useCallback((checked) => {
    setStartingLocationDifferent(checked)
  }, [])

  // Form initial values
  const formInitialValues = {
    ...initialValues, // Shell user data (email, firstname, lastname)
    cell_phone: '', // Fix javascript warning
  }

  // Password validation rules
  const passwordRules = [
    {
      min: 8,
      required: true,
      message: 'Password must be at least 8 characters',
    },
    () => ({
      validator() {
        return Promise.resolve()
      },
    }),
  ]

  const confirmPasswordRules = [
    {
      required: true,
      message: 'Please confirm your password',
    },
    ({ getFieldValue }) => ({
      validator(_, value) {
        if (!value || getFieldValue('password') === value) {
          return Promise.resolve()
        }
        return Promise.reject(new Error('The two passwords that you entered do not match'))
      },
    }),
  ]

  const passwordRequirementsRules = [
    () => ({
      validator() {
        if (passwordRequirementsMet) {
          return Promise.resolve()
        }
        return Promise.reject(new Error('Please meet all password requirements'))
      },
    }),
  ]

  const termsRules = [
    () => ({
      validator() {
        if (agreed) {
          return Promise.resolve()
        }
        return Promise.reject(new Error('You must agree to the terms and conditions.'))
      },
    }),
  ]

  // Extract JWT to prevent object reference changes
  const shellUserJWT = shellUser?.jwt

  // Coverage props - memoized to prevent repeated calls
  const getMapOverlayWithJWT = useCallback((type, states, counties) =>
    getMapOverlay(type, states, counties, shellUserJWT), [shellUserJWT])

  const coverageProps = {
    showNationalView: false,
    onCoverageChange: setCoverage,
    showError: coverageError,
    currentCoverageInfo: {
      selectedCoverage: coverage,
      selectedCoverageLevel: coverageLevel,
    },
    getMapOverlayAction: getMapOverlayWithJWT,
    zipOverlay,
    countyOverlay,
  }

  return (
    <PropertyDataCollectorSetup
      formRef={formRef}
      coverageAreaRef={coverageAreaRef}
      submitting={submitting}
      validationErrors={validationErrors}
      agreed={agreed}
      termsModalVisible={termsModalVisible}
      formValues={formValues}
      formInitialValues={formInitialValues}
      passwordRules={passwordRules}
      confirmPasswordRules={confirmPasswordRules}
      passwordRequirementsRules={passwordRequirementsRules}
      termsRules={termsRules}
      coverageProps={coverageProps}
      credentials={credentials}
      startingLocationDifferent={startingLocationDifferent}
      onSubmit={() => handleSinglePageFormSubmit(formRef, credentialsRef.current, coverage, coverageLevel)}
      onFormValuesChange={handleFormValuesChange}
      onPasswordRequirementsChange={handlePasswordRequirementsChange}
      onCredentialsChange={handleCredentialsChange}
      onTermsAgreed={setTermsAgreed}
      onAgree={handleAgree}
      onDisagree={handleDisagree}
      onOpenTermsModal={openTermsModal}
      onStartingLocationChange={handleStartingLocationChange}
      onClearValidationErrors={onClearValidationErrors}
      shellUser={shellUser}
    />
  )
}

export default PropertyDataCollectorSetupContainer
